import {
  CART_INFO_KEY,
  MODES,
  SEL,
  EVENTS,
  TIMING,
  debug,
  debounce,
  cssEscape,
  SelectionState,
  normalizeMode,
  normalizeStore,
  stringifyLineCartInfo,
  readStoreFromItem,
  parseInventory,
  getStockStatus,
  $,
  setStoreView,
  activateTab,
  createStoreList,
  analytics,
  dispatchStoreChanged,
} from 'store-selector-core';
const extractStockStatus = (stockEl, labelEl) => {
  if (!stockEl) return null;

  const cssClass =
    Array.from(stockEl.classList)
      .find((className) => className.startsWith('store-selector__stock--'))
      ?.replace('store-selector__stock--', '') || 'out-of-stock';
  return {
    cssClass,
    label: labelEl?.textContent?.trim() || window.theme.strings.storeSelector.outOfStock,
  };
};

class PdpInventory {
  constructor(host, getDrawer) {
    this.host = host;
    this.getDrawer = getDrawer;
    this.inventoryData = null;
    this.productForm = null;
    this.submitBtn = null;
    this.formError = null;
  }

  connect({ productForm, submitBtn, formError }) {
    this.productForm = productForm || null;
    this.submitBtn = submitBtn || null;
    this.formError = formError || null;
  }

  resetInventory() {
    this.inventoryData = null;
  }

  #parseInventory() {
    this.inventoryData = parseInventory(this.getDrawer()?.querySelector('[data-store-inventory]'));
  }

  getStoreStockStatus(storeId) {
    const drawer = this.getDrawer();
    if (!drawer || !storeId) return null;

    if (!this.inventoryData) this.#parseInventory();

    if (this.inventoryData?.inventories && Object.keys(this.inventoryData.inventories).length > 0) {
      const quantity = this.inventoryData.inventories[storeId] ?? this.inventoryData.inventories[Number(storeId)];
      if (quantity !== undefined) {
        return getStockStatus(Number(quantity), this.inventoryData.thresholdLow, this.inventoryData.thresholdVeryLow);
      }
    }

    const item = drawer.querySelector(`${SEL.storeItem}[data-store-id="${cssEscape(storeId)}"]`);
    return extractStockStatus(item?.querySelector('[data-role="store-stock"]'), item?.querySelector('[data-role="store-stock-label"]'));
  }

  getDeliveryStockStatus() {
    return extractStockStatus(this.host.querySelector('[data-role="delivery-stock"]'), this.host.querySelector('[data-role="delivery-stock-label"]'));
  }

  isSelectedStoreInStock(store) {
    if (!store?.id) return false;

    const status = this.getStoreStockStatus(store.id);
    return Boolean(status && status.cssClass !== 'out-of-stock');
  }

  isDeliveryInStock() {
    const status = this.getDeliveryStockStatus();
    return Boolean(status && status.cssClass !== 'out-of-stock');
  }

  #ensureInput(key) {
    if (!this.productForm) return null;

    const fieldName = `properties[${key}]`;
    let input = this.productForm.querySelector(`input[name="${fieldName}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = fieldName;
      this.productForm.appendChild(input);
    }

    return input;
  }

  #setProperty(key, value) {
    const input = this.#ensureInput(key);
    if (input) input.value = value;
  }

  #removeProperty(key) {
    if (!this.productForm) return;

    const fieldName = `properties[${key}]`;
    this.productForm.querySelector(`input[name="${fieldName}"]`)?.remove();
  }

  syncLineItemProperties(mode, selectedStore) {
    if (!this.productForm) return;

    const activeStore = mode === MODES.CLICK_COLLECT ? selectedStore : null;
    const itemFulfillmentType = (this.host?.dataset.itemFulfillmentType || '').trim();
    this.#setProperty(CART_INFO_KEY, stringifyLineCartInfo(activeStore ? MODES.CLICK_COLLECT : MODES.DELIVERY, activeStore));
    if (itemFulfillmentType) this.#setProperty('_item_fulfillment_type', itemFulfillmentType);
    else this.#removeProperty('_item_fulfillment_type');
  }

  #setError(message) {
    if (!this.formError) return;

    if (message) {
      this.formError.textContent = message;
      this.formError.hidden = false;
      this.formError.dataset.storeSelector = 'true';
      return;
    }

    if (this.formError.dataset.storeSelector === 'true') {
      this.formError.textContent = '';
      this.formError.hidden = true;
      delete this.formError.dataset.storeSelector;
    }
  }

  isClickCollectUnavailable() {
    const clickCollectPanel = this.host?.querySelector('[data-role="click-collect-panel"]');
    return clickCollectPanel?.dataset.clickCollectUnavailable === 'true';
  }

  isDeliveryUnavailable() {
    const deliveryPanel = this.host?.querySelector('[data-role="delivery-panel"]');
    return deliveryPanel?.dataset.deliveryUnavailable === 'true';
  }

  #computeBlockedState(mode, selectedStore) {
    if (mode === MODES.CLICK_COLLECT) {
      if (this.isClickCollectUnavailable()) return { blocked: true, message: window.theme.strings.storeSelector.clickCollectUnavailable || '' };

      if (!selectedStore?.id) return { blocked: true, message: window.theme.strings.storeSelector.addToCartSelectStoreError };

      if (!this.isSelectedStoreInStock(selectedStore)) return { blocked: true, message: window.theme.strings.storeSelector.addToCartOutOfStockError };
    } else if (this.isDeliveryUnavailable()) {
      return { blocked: true, message: window.theme.strings.storeSelector.deliveryUnavailable || '' };
    } else if (!this.isDeliveryInStock()) {
      return { blocked: true, message: '' };
    }

    return { blocked: false, message: '' };
  }

  #setQuantityDisabled(disabled) {
    const quantityInput = this.productForm?.querySelector('quantity-input');
    if (!quantityInput) return;

    for (const btn of quantityInput.querySelectorAll('.qty-input__btn')) {
      btn.disabled = disabled;
    }

    const input = quantityInput.querySelector('.qty-input__input');
    if (input) input.disabled = disabled;
  }

  syncAddToCart(mode, selectedStore) {
    this.syncLineItemProperties(mode, selectedStore);
    if (!this.submitBtn || this.submitBtn.classList.contains('is-loading')) return;

    const { blocked, message } = this.#computeBlockedState(mode, selectedStore);

    if (blocked) {
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.dataset.storeSelectorBlocked = 'true';
      this.#setQuantityDisabled(true);
      this.#setError(message);
      return;
    }

    if (this.submitBtn.dataset.storeSelectorBlocked === 'true') {
      this.submitBtn.removeAttribute('aria-disabled');
      delete this.submitBtn.dataset.storeSelectorBlocked;
      this.#setQuantityDisabled(false);
    }

    this.#setError('');
  }
}

class StoreSelectorPdp extends HTMLElement {
  constructor() {
    super();
    this.state = new SelectionState();
    this.drawer = null;
    this.productRoot = null;
    this.productForm = null;
    this.variantEventTarget = null;
    this.boundDrawer = null;
    this.variantRequestToken = 0;
    this.variantRequestController = null;
    this.inventory = new PdpInventory(this, () => this.drawer);
    this.list = createStoreList({
      getPanel: () => this.drawer,
      isInStock: (item) => {
        if (!item) return true;

        if (item.dataset.available !== undefined) return item.dataset.available === 'true';

        const stock = item.querySelector('[data-role="store-stock"]');
        return !stock || !stock.classList.contains('store-selector__stock--out-of-stock');
      },
    });
    this.#applySearchDebounced = debounce((value) => this.list.applySearch(value), TIMING.searchDebounce);
  }

  connectedCallback() {
    this.#disconnect();
    this.drawer = this.#resolveDrawer();
    this.productRoot = this.closest('.js-product');
    const scope = this.closest('.product-info') || this.closest('quick-add-drawer');
    this.productForm = scope?.querySelector('.js-product-form-main') || null;
    const submitBtn = this.productForm?.querySelector('[name="add"]') || null;
    const formError = scope?.querySelector('product-form .js-form-error') || null;
    this.inventory.connect({ productForm: this.productForm, submitBtn, formError });
    this.state.restore(this.#getServerSelection());
    this.#reconcileModeWithAvailability();
    this.#normalizeStateForAvailableModes();
    if (this.drawer) this.list.attach(this.drawer);

    this.#bindEvents();
    this.#syncSelectionUi();
    this.#autoOpenFromUrlParam();
  }

  disconnectedCallback() {
    this.#disconnect();
  }

  #disconnect() {
    this.#abortVariantRequest();
    this.#unbindEvents();
  }

  getFulfillmentMode() {
    return this.state.getMode() === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
  }

  getSelectedStore() {
    return this.state.getSelectedStore();
  }

  #supportsClickCollect() {
    return Boolean(this.querySelector('[role="tab"][data-fulfillment="click_collect"]'));
  }

  #supportsDelivery() {
    return Boolean(this.querySelector('[role="tab"][data-fulfillment="delivery"]'));
  }

  #reconcileModeWithAvailability() {
    if (this.getFulfillmentMode() === MODES.DELIVERY && this.inventory.isDeliveryUnavailable() && this.#supportsClickCollect()) {
      this.state.mode = MODES.CLICK_COLLECT;
      return;
    }

    if (this.getFulfillmentMode() === MODES.CLICK_COLLECT && this.inventory.isClickCollectUnavailable() && this.#supportsDelivery()) {
      this.state.mode = MODES.DELIVERY;
    }
  }

  #normalizeStateForAvailableModes() {
    const supportsDelivery = this.#supportsDelivery();
    const supportsClickCollect = this.#supportsClickCollect();

    if (!supportsDelivery && supportsClickCollect) {
      this.state.mode = MODES.CLICK_COLLECT;
      return;
    }

    // Delivery-only or neither mode available: force delivery and clear store
    if (!supportsClickCollect) {
      this.state.mode = MODES.DELIVERY;
      this.state.store = null;
      this.state.pendingStore = null;
    }
  }

  #getServerSelection() {
    return {
      committed: this.dataset.cartCommitted !== 'false',
      mode: this.dataset.initialFulfillmentMode || MODES.DELIVERY,
      store: {
        id: this.dataset.initialStoreId,
        name: this.dataset.initialStoreName || '',
        address: this.dataset.initialStoreAddress || '',
        phone: this.dataset.initialStorePhone || '',
        salonPhone: this.dataset.initialSalonPhone || '',
      },
    };
  }

  #resolveDrawer() {
    const block = this.closest('.product-info__block');
    const scopedDrawer = block ? $(SEL.pdpDrawer, block) : null;
    return scopedDrawer
      || this.closest('.product-info')?.querySelector(SEL.pdpDrawer)
      || this.closest('quick-add-drawer')?.querySelector(SEL.pdpDrawer)
      || null;
  }

  #storeResolver = (storeId, fallback) => this.list.getStoreById(storeId, fallback);

  #applySearchDebounced;

  #syncSelectionUi() {
    this.#syncUI();
    this.inventory.syncAddToCart(this.getFulfillmentMode(), this.getSelectedStore());
  }

  #bindDrawerEvents(drawer = this.drawer) {
    if (!drawer || this.boundDrawer === drawer) return;

    this.boundDrawer = drawer;
    this.boundDrawer.addEventListener('change', this.#onDrawerChange);
    this.boundDrawer.addEventListener('click', this.#onDrawerClick);
    this.boundDrawer.addEventListener('input', this.#onDrawerInput);
    this.boundDrawer.addEventListener('on:store-selector:before-close', this.#onDrawerBeforeClose);
  }

  #unbindDrawerEvents() {
    if (!this.boundDrawer) return;

    this.boundDrawer.removeEventListener('change', this.#onDrawerChange);
    this.boundDrawer.removeEventListener('click', this.#onDrawerClick);
    this.boundDrawer.removeEventListener('input', this.#onDrawerInput);
    this.boundDrawer.removeEventListener('on:store-selector:before-close', this.#onDrawerBeforeClose);
    this.boundDrawer = null;
  }

  #ensureDrawerBinding() {
    const resolvedDrawer = this.#resolveDrawer();
    if (resolvedDrawer === this.drawer && this.boundDrawer === resolvedDrawer) return this.drawer;

    this.#unbindDrawerEvents();
    this.drawer = resolvedDrawer;
    this.#bindDrawerEvents(resolvedDrawer);
    return this.drawer;
  }

  #bindEvents() {
    this.addEventListener('click', this.#onRootClick);
    this.addEventListener('keydown', this.#onRootKeydown);
    this.addEventListener('on:tab:activated', this.#onTabActivated);
    document.addEventListener(EVENTS.STORE_CHANGED, this.#onExternalStoreChanged);
    this.#ensureDrawerBinding();

    this.variantEventTarget = this.productRoot || document;
    this.variantEventTarget.addEventListener('on:variant:change', this.#onVariantChange);
  }

  #unbindEvents() {
    this.removeEventListener('click', this.#onRootClick);
    this.removeEventListener('keydown', this.#onRootKeydown);
    this.removeEventListener('on:tab:activated', this.#onTabActivated);
    document.removeEventListener(EVENTS.STORE_CHANGED, this.#onExternalStoreChanged);
    this.#unbindDrawerEvents();

    if (this.variantEventTarget) {
      this.variantEventTarget.removeEventListener('on:variant:change', this.#onVariantChange);
      this.variantEventTarget = null;
    }
  }

  #routeDrawerAction(evt, { preventDefault = false, requireRole = false } = {}) {
    const roleSuffix = requireRole ? '[role="button"]' : '';

    const filteredOpen = evt.target.closest(SEL.openDrawerFiltered);
    if (filteredOpen) {
      if (preventDefault) evt.preventDefault();

      this.#openDrawer(filteredOpen, { onlyInStock: true });
      return true;
    }

    const row = evt.target.closest(`${SEL.openDrawer}${roleSuffix}`);
    if (row) {
      if (preventDefault) evt.preventDefault();

      this.#openDrawer(row);
      return true;
    }

    const changeButton = evt.target.closest(`${SEL.changeStoreButton}${roleSuffix}`);
    if (changeButton) {
      evt.preventDefault();
      this.#openDrawer(changeButton);
      return true;
    }

    return false;
  }

  #onTabActivated = (evt) => {
    const tab = evt.detail?.tab;
    if (tab) this.#handleTabClick(tab);
  };

  #onRootClick = (evt) => {
    if (evt.defaultPrevented) return;

    const tab = evt.target.closest('[role="tab"]');
    if (tab) {
      /* When the tabbed-content component is upgraded it re-dispatches user
         activations (click and keyboard) as on:tab:activated, which is handled
         above. Only route raw clicks when that component is unavailable. */
      const tabbedContent = tab.closest('tabbed-content');
      if (!tabbedContent || typeof tabbedContent.activateTab !== 'function') this.#handleTabClick(tab);

      return;
    }

    this.#routeDrawerAction(evt);
  };

  #onRootKeydown = (evt) => {
    if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;

    this.#routeDrawerAction(evt, { preventDefault: true, requireRole: true });
  };

  #onDrawerChange = (evt) => {
    const stockToggle = evt.target.closest(SEL.stockFilterToggle);
    if (stockToggle) {
      this.list.setStockFilter(stockToggle.checked);
      return;
    }

    const radio = evt.target.closest(SEL.storeRadio);
    if (radio) this.#handleStoreRadioChange(radio);
  };

  #onDrawerClick = (evt) => {
    if (evt.target.closest(SEL.searchTriggerLocation)) {
      evt.preventDefault();
      this.list.requestGeolocation();
      return;
    }

    if (evt.target.closest(SEL.confirmStore)) {
      void this.#confirmPendingStore();
      return;
    }

    if (evt.target.closest(SEL.cancelStore) || evt.target.closest('[data-action="close-store-selector"]')) {
      this.#cancelPendingStore();
    }
  };

  #onDrawerInput = (evt) => {
    const input = evt.target.closest(SEL.searchInput);
    if (input) this.#applySearchDebounced(input.value || '');
  };

  #onDrawerBeforeClose = () => {
    this.#cancelPendingStore();
  };

  #onVariantChange = (evt) => {
    if (this.productRoot && !this.productRoot.contains(evt.target)) return;

    const { variant } = evt.detail || {};
    if (variant) void this.#handleVariantChange(variant);
  };

  #onExternalStoreChanged = (evt) => {
    const { store, fulfillmentMode, _source } = evt.detail || {};
    if (_source !== 'cart-click-collect') return;

    this.state.mode = normalizeMode(fulfillmentMode);
    this.state.store = normalizeStore(store);
    this.state.pendingStore = null;
    this.#normalizeStateForAvailableModes();
    this.state.persistCommittedSelection({ storeResolver: this.#storeResolver });
    this.#syncSelectionUi();
  };

  #handleTabClick(tab) {
    const mode = tab.dataset.fulfillment;
    if (!mode || mode === this.getFulfillmentMode()) return;

    if (tab.getAttribute('aria-selected') !== 'true') activateTab(this, mode);

    void this.#setMode(mode);
  }

  async #setMode(mode) {
    if (mode === MODES.CLICK_COLLECT && !this.#supportsClickCollect()) {
      this.state.mode = MODES.DELIVERY;
      this.state.store = null;
      this.state.pendingStore = null;
      this.#syncSelectionUi();
      return;
    }

    if (mode === MODES.DELIVERY && !this.#supportsDelivery()) {
      this.state.mode = MODES.CLICK_COLLECT;
      this.#syncSelectionUi();
      return;
    }

    if (mode === MODES.DELIVERY) {
      this.state.mode = MODES.DELIVERY;
      this.state.pendingStore = null;
      this.#syncSelectionUi();
      return;
    }

    this.state.mode = MODES.CLICK_COLLECT;
    if (!this.state.store?.id) {
      this.state.store = normalizeStore(this.state.getRememberedStore(this.#storeResolver));
    }

    this.#syncSelectionUi();
  }

  #openDrawerInternal(opener, { onlyInStock } = {}) {
    const drawer = this.#ensureDrawerBinding();
    if (!drawer) return false;

    if (typeof onlyInStock === 'boolean') this.list.stockFilterEnabled = onlyInStock;

    this.list.attach(drawer, { preserveState: true });
    this.list.syncStockFilterToggle();
    this.list.highlight(this.state.store?.id || null);
    drawer.open(opener);
    const searchInput = $(SEL.searchInput, drawer);
    if (searchInput?.value) this.list.applySearch(searchInput.value);

    this.list.requestGeolocationIfGranted();

    analytics('view_list', {
      source: 'pdp',
      store_count: this.list.getStoreItems(drawer).length,
    });

    return true;
  }

  #openDrawer(opener, { onlyInStock } = {}) {
    if (this.#openDrawerInternal(opener, { onlyInStock })) return;

    window.requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (this.#openDrawerInternal(opener, { onlyInStock })) return;

      debug.error('open_drawer_missing', 'Store selector drawer not found', {
        productId: this.dataset.productId || null,
        variantId: this.dataset.variantId || null,
        inQuickAdd: Boolean(this.closest('quick-add-drawer')),
      });
    });
  }

  #handleStoreRadioChange(radio) {
    const store = readStoreFromItem(radio.closest(SEL.storeItem));
    if (!store) return;

    this.state.pendingStore = normalizeStore(store);
    this.list.highlight(store.id);
    this.#updateConfirmButton();
  }

  #updateConfirmButton() {
    const drawer = this.drawer || this.#ensureDrawerBinding();
    if (!drawer) return;

    const footer = $(SEL.storeFooter, drawer);
    const confirmButton = $(SEL.confirmStore, drawer);
    const hasPending = Boolean(this.state.pendingStore);
    if (footer) footer.classList.toggle('drawer__footer--out', !hasPending);

    if (confirmButton) confirmButton.disabled = !hasPending;
  }

  async #confirmPendingStore() {
    if (!this.state.pendingStore) return;

    const store = normalizeStore(this.state.pendingStore);
    if (!store) return;

    this.#selectStore(store);
    if (this.drawer) this.drawer.close();
  }

  #cancelPendingStore() {
    this.state.pendingStore = null;
    this.list.highlight(this.state.store?.id || null);
    this.#updateConfirmButton();
  }

  #selectStore(store) {
    const previousStoreId = this.state.store?.id || null;
    this.state.mode = MODES.CLICK_COLLECT;
    this.state.store = normalizeStore(store);
    this.state.pendingStore = null;
    this.state.persistCommittedSelection({ storeResolver: this.#storeResolver });
    this.#syncSelectionUi();
    dispatchStoreChanged(this.state.store, previousStoreId, MODES.CLICK_COLLECT, { _source: 'pdp' });
    analytics(previousStoreId ? 'change_store' : 'select_store', {
      source: 'pdp',
      store_id: store.id,
      previous_store_id: previousStoreId,
    });
  }

  /**
   * If the URL contains ?open_store_selector=true, auto-switch to Click & Collect
   * and open the store drawer. Used when linking from the quick-add drawer for
   * variants that are OOS online but available in stores.
   */
  #autoOpenFromUrlParam() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_store_selector') !== 'true') return;

    // Clean up the URL param so a refresh doesn't re-trigger
    params.delete('open_store_selector');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState(null, '', cleanUrl);

    if (!this.#supportsClickCollect() || !this.drawer) return;

    this.state.mode = MODES.CLICK_COLLECT;
    this.#syncUI();
    this.#openDrawer(this, { onlyInStock: true });
  }

  #syncUI() {
    this.#normalizeStateForAvailableModes();
    const activeMode = this.getFulfillmentMode();

    activateTab(this, activeMode);

    const hasSelectedStore = activeMode === MODES.CLICK_COLLECT && Boolean(this.state.store?.id);
    const defaultView = $(SEL.clickCollectDefault, this);
    if (defaultView) defaultView.hidden = hasSelectedStore;

    setStoreView(this, '[data-store-selected-view]', hasSelectedStore ? this.state.store.id : null);
    this.list.highlight(this.state.store?.id || null);
    this.#updateConfirmButton();
  }

  #abortVariantRequest() {
    if (!this.variantRequestController) return;

    this.variantRequestController.abort();
    this.variantRequestController = null;
  }

  async #handleVariantChange(variant) {
    const drawer = this.#ensureDrawerBinding();
    if (!variant || !drawer) return;

    this.dataset.variantId = variant.id;
    let rootUrl = this.dataset.rootUrl || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    const searchInput = $(SEL.searchInput, drawer);
    const searchValue = searchInput?.value || this.list.searchQuery;
    const requestToken = ++this.variantRequestToken;
    this.#abortVariantRequest();
    this.variantRequestController = new AbortController();
    try {
      const response = await fetch(`${rootUrl}variants/${variant.id}/?section_id=store-selector-drawer`, {
        signal: this.variantRequestController.signal,
      });
      if (!response.ok || requestToken !== this.variantRequestToken) return;

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      if (requestToken !== this.variantRequestToken) return;

      const newPanels = $(SEL.pdpPanels, template.content);
      const currentPanels = $(SEL.pdpPanels, this);
      if (newPanels && currentPanels) currentPanels.replaceWith(newPanels);

      const newProductSummary = $(SEL.pdpProductSummary, template.content);
      const currentProductSummary = $(SEL.pdpProductSummary, drawer);
      if (newProductSummary && currentProductSummary) currentProductSummary.replaceWith(newProductSummary);

      const newDrawerContent = $(SEL.pdpDrawerContent, template.content);
      const currentDrawerContent = $(SEL.pdpDrawerContent, drawer);
      if (newDrawerContent && currentDrawerContent) currentDrawerContent.replaceWith(newDrawerContent);

      this.inventory.resetInventory();
      this.list.attach(drawer, { preserveState: true });
      if (searchInput) searchInput.value = searchValue;

      this.#reconcileModeWithAvailability();
      this.#normalizeStateForAvailableModes();
      this.#syncSelectionUi();
      if (this.list.userCoords) this.list.applyCoords(this.list.userCoords);
      else this.list.applySearch(searchValue);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        analytics('error', { source: 'pdp', code: 'variant_data_reload_failed' });
      }
    } finally {
      if (requestToken === this.variantRequestToken) this.variantRequestController = null;
    }
  }
}

if (!customElements.get('store-selector-pdp')) {
  customElements.define('store-selector-pdp', StoreSelectorPdp);
}

export default StoreSelectorPdp;
